/**
 * Almacenamiento persistente y estado de guardado.
 *
 * Android puede DESALOJAR IndexedDB bajo presión de almacenamiento, y con ella
 * se irían las sesiones, el Learning Bank y los blobs de audio. Pedir
 * persistencia es lo que lo evita, y Chrome se la concede casi siempre a una
 * PWA instalada.
 *
 * Hasta la Fase 8 no hay sincronización, así que el estado se limita a lo que
 * de verdad se puede saber: si hay algo guardado y si el almacenamiento es
 * persistente. Nada de inventar un «Sincronizando» que todavía no existe.
 */

export type EstadoAlmacenamiento =
  | 'comprobando'
  | 'persistente'
  | 'no_persistente'
  | 'no_soportado';

/**
 * Pide al navegador que no desaloje los datos.
 *
 * Es idempotente: si ya está concedido, `persist()` devuelve `true` sin
 * volver a preguntar. No muestra ningún diálogo en Chrome.
 */
export async function solicitarPersistencia(): Promise<EstadoAlmacenamiento> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.storage ||
    typeof navigator.storage.persist !== 'function'
  ) {
    return 'no_soportado';
  }

  try {
    if (typeof navigator.storage.persisted === 'function') {
      const yaConcedido = await navigator.storage.persisted();
      if (yaConcedido) return 'persistente';
    }
    const concedido = await navigator.storage.persist();
    return concedido ? 'persistente' : 'no_persistente';
  } catch {
    return 'no_soportado';
  }
}

export interface UsoDeAlmacenamiento {
  usadoBytes: number | null;
  cuotaBytes: number | null;
}

export async function consultarUso(): Promise<UsoDeAlmacenamiento> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.storage ||
    typeof navigator.storage.estimate !== 'function'
  ) {
    return { usadoBytes: null, cuotaBytes: null };
  }
  try {
    const { usage, quota } = await navigator.storage.estimate();
    return { usadoBytes: usage ?? null, cuotaBytes: quota ?? null };
  } catch {
    return { usadoBytes: null, cuotaBytes: null };
  }
}

export function formatearBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
